import {
  ChakraBox as Box,
  ChakraFlex as Flex,
  ChakraGrid as Grid,
  ChakraGridItem as GridItem,
  DateFormat,
  Text,
  cornerRadius,
} from '@equip.health/ui';
import { ReactNode } from 'react';

import { DateTime } from 'luxon';

import AssessmentCardActionMessage, {
  CompleteTogetherBannerVariant,
} from './AssessmentCardActionMessage';
import AssessmentCardHeader from './AssessmentCardHeader';
import AssessmentCardListItem from './AssessmentCardListItem';
import { AssessmentFrequency } from './AssessmentFrequency';
import { AssessmentListCardItem } from './types';

export interface AssessmentListCardProps {
  assessmentsResponses: AssessmentListCardItem[]; // sorted most recent to oldest
  assessmentName: string;
  frequency: AssessmentFrequency;
  descriptionText: string;
  id: string;
  isHistoryEnabled: boolean;
  isAssessmentActive: boolean;
  onCreateNewClick: (source: 'title' | 'kebab') => void;
  onDeleteClick: (assessmentResponseId: string) => void;
  onDownloadClick: (assessmentResponseId: string) => void;
  onCardHeaderResumeClick: (assessmentResponseId: string) => void;
  onListItemResumeClick: (assessmentResponseId: string) => void;
  onCompleteTogetherClick: (
    assessmentResponseId: string,
    bannerVariant: CompleteTogetherBannerVariant,
  ) => void;
  onHeaderViewClick: (assessmentResponseId: string) => void;
  onListItemViewClick: (assessmentResponseId: string) => void;
  onViewHistoryClick: () => void;
  titleRow: string[];
  avatarTooltipWrapper?: (props: {
    children: ReactNode;
    providerExternalId: string;
  }) => ReactNode;
}

const idPrefix = 'assessment-list-card';
const AssessmentListCard = (
  // eslint-disable-next-line no-restricted-syntax -- grandfathered in before this rule was added about object.properties.length limits
  {
    assessmentsResponses,
    assessmentName,
    descriptionText,
    frequency,
    id,
    isAssessmentActive,
    isHistoryEnabled,
    onCreateNewClick,
    onViewHistoryClick,
    onHeaderViewClick,
    onListItemViewClick,
    onDeleteClick,
    onDownloadClick,
    onCardHeaderResumeClick,
    onListItemResumeClick,
    onCompleteTogetherClick,
    titleRow,
    avatarTooltipWrapper,
  }: AssessmentListCardProps,
) => {
  const slicedAssessments = assessmentsResponses.slice(-4);
  const mostRecentRecord: AssessmentListCardItem | undefined =
    slicedAssessments[0];
  return (
    <Grid
      background="neutral.background.primary"
      borderColor="neutral.line"
      borderRadius={cornerRadius.large}
      borderWidth="1px"
      gridTemplateAreas={`"header header" "titleRow titleRow" "list list" "action-message action-message"`}
      gridTemplateColumns="160px 1fr"
      gridTemplateRows="50px 1fr min-content"
      id={`${idPrefix}-container__${id}`}
      padding="24px"
    >
      {titleRow?.length > 0 && (
        <GridItem area="titleRow">
          <Flex
            alignItems="center"
            background="neutral.background.secondary"
            gap="18px"
            height="60px"
            textAlign="center"
          >
            <Box flex="0 0 88px" /> {/* status tag */}
            <Box flex="0 0 72px" /> {/* action button */}
            <Flex flex="1" justifyContent="space-evenly" minWidth="182px">
              {titleRow.map((title) => (
                <Box
                  flex="0 0 67px"
                  key={`assessment-list-card-${id}-${title}`}
                >
                  <Text color="neutral.secondary" variant="caption">
                    {title}
                  </Text>
                </Box>
              ))}
            </Flex>
            <Flex flex="1" minWidth="136px" /> {/* date */}
            <Flex flex="1" /> {/* respondent */}
          </Flex>
        </GridItem>
      )}
      <GridItem area="header" marginBottom="18px">
        <AssessmentCardHeader
          assessmentName={assessmentName}
          descriptionText={descriptionText}
          frequency={frequency}
          isAssessmentActive={isAssessmentActive}
          isHistoryEnabled={isHistoryEnabled}
          mostRecentAssessmentResponseId={mostRecentRecord?.externalId}
          mostRecentAssessmentStatus={mostRecentRecord?.status}
          onCreateNewClick={onCreateNewClick}
          onDeleteClick={onDeleteClick}
          onResumeClick={onCardHeaderResumeClick}
          onViewAssessmentClick={onHeaderViewClick}
          onViewHistoryClick={onViewHistoryClick}
        />
      </GridItem>
      <GridItem area="list">
        {slicedAssessments.map((assessment) => (
          <AssessmentCardListItem
            assessmentResponseId={assessment.externalId}
            assignedTo={assessment.assignedTo}
            avatarTooltipWrapper={avatarTooltipWrapper}
            createdDate={assessment.createdDate}
            endDate={assessment.endDate}
            key={assessment.externalId}
            onDeleteClick={onDeleteClick}
            onDownloadClick={onDownloadClick}
            onResumeClick={onListItemResumeClick}
            onViewAssessmentClick={onListItemViewClick}
            responseUpdatedBy={assessment.responseUpdatedBy}
            responseUpdatedByAvatarSrc={assessment.responseUpdatedByAvatarSrc}
            responseUpdatedByUserId={assessment.responseUpdatedByUserId}
            responseUpdatedByUserType={assessment.responseUpdatedByUserType}
            riskCategories={assessment.riskCategories}
            scores={assessment.scores}
            status={assessment.status}
            submittedDate={assessment.submittedDate}
          />
        ))}
      </GridItem>
      {mostRecentRecord && (
        <AssessmentCardActionMessage
          assessmentResponseId={mostRecentRecord.externalId}
          assignee={mostRecentRecord.assignedTo}
          epochCreatedDate={DateTime.fromFormat(
            mostRecentRecord.createdDate,
            DateFormat.longDateWithTime,
          ).toMillis()}
          frequency={frequency}
          onCompleteTogetherClick={onCompleteTogetherClick}
          responseUpdatedBy={mostRecentRecord.responseUpdatedBy}
          status={mostRecentRecord.status}
        />
      )}
    </Grid>
  );
};

export default AssessmentListCard;
